import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ProjectService as ps } from '../api/ProjectService';
import { Project } from '../api/protosCompiled/project/project_pb';
import { useSyncInvalidation } from './useSyncConnector';
import { useFetchUser } from './useFetchUsers';

/**
 * Hook to fetch projects with multi-screen synchronization support
 */
export function useSyncedProjects() {
  const projectsQueryKey = ['projects'];
  const estimatesQueryKey = ['estimates'];
  const countedAssembliesQueryKey = ['countedAssemblies'];
  const projectFoldersQueryKey = ['projectFolders'];
  const assembliesByFileIdQueryKey = ['assembliesByFileId'];
  const estimateBreakoutsQueryKey = ['estimateBreakouts'];
  const changeOrderBreakoutsQueryKey = ['changeOrderBreakouts'];

  // Standard React Query hook for fetching projects
  const { data, error, isLoading, refetch } = useQuery(projectsQueryKey, async () => {
    const response = await ps.fetchProjects();
    return response.projectsList || [];
  });

  // Get the invalidation function to propagate changes across screens
  const { invalidateSync } = useSyncInvalidation();

  // Function to invalidate all relevant queries across all screens/components
  const invalidateAll = () => {
    invalidateSync(projectsQueryKey);
    invalidateSync(estimatesQueryKey);
    invalidateSync(countedAssembliesQueryKey);
    invalidateSync(projectFoldersQueryKey);
    invalidateSync(assembliesByFileIdQueryKey);
    invalidateSync(estimateBreakoutsQueryKey);
    invalidateSync(changeOrderBreakoutsQueryKey);
  };

  return {
    projects: data,
    error,
    isLoading,
    refetch,
    invalidateAll,
  };
}

/**
 * Hook to create projects with multi-screen sync support
 */
export function useSyncedCreateProjectMutation() {
  const queryClient = useQueryClient();
  const { invalidateSync } = useSyncInvalidation();
  const { data: user } = useFetchUser();
  const projectsQueryKey = ['projects'];
  const projectFoldersQueryKey = ['projectFolders'];
  const projectHierarchyQueryKey = ['projectHierarchy', user?.user?.id];

  return useMutation((data: Project.AsObject) => ps.createProject(data), {
    onSuccess: () => {
      // Local invalidation
      queryClient.invalidateQueries(projectsQueryKey);
      queryClient.invalidateQueries(projectFoldersQueryKey);
      queryClient.invalidateQueries(projectHierarchyQueryKey);

      // Propagate invalidation to other screens/components
      invalidateSync(projectsQueryKey);
      invalidateSync(projectFoldersQueryKey);
      invalidateSync(projectHierarchyQueryKey);
    },
    onError: error => {
      console.error('Error creating project', error);
    },
  });
}

/**
 * Hook to update projects with multi-screen sync support
 */
export function useSyncedUpdateProjectMutation() {
  const queryClient = useQueryClient();
  const { invalidateSync } = useSyncInvalidation();
  const { data: user } = useFetchUser();
  const projectsQueryKey = ['projects'];
  const projectFoldersQueryKey = ['projectFolders'];
  const projectHierarchyQueryKey = ['projectHierarchy', user?.user?.id];

  return useMutation((data: Project.AsObject) => ps.updateProject(data), {
    onSuccess: () => {
      // Local invalidation
      queryClient.invalidateQueries(projectsQueryKey);
      queryClient.invalidateQueries(projectFoldersQueryKey);
      queryClient.invalidateQueries(projectHierarchyQueryKey);

      // Propagate invalidation to other screens/components
      invalidateSync(projectsQueryKey);
      invalidateSync(projectFoldersQueryKey);
      invalidateSync(projectHierarchyQueryKey);
    },
    onError: error => {
      console.error('Error updating project', error);
    },
  });
}

/**
 * Since there's no deleteProject method in the ProjectService,
 * this hook is provided as a template for when the method is implemented.
 * For now, it returns a mock implementation.
 */
export function useSyncedDeleteProjectMutation() {
  const queryClient = useQueryClient();
  const { invalidateSync } = useSyncInvalidation();
  const projectsQueryKey = ['projects'];
  const projectFoldersQueryKey = ['projectFolders'];

  return useMutation(
    (projectId: string) => {
      console.warn('deleteProject method is not implemented in ProjectService');
      // Return a mock promise for now
      return Promise.resolve({ success: true });
    },
    {
      onSuccess: () => {
        // Local invalidation
        queryClient.invalidateQueries(projectsQueryKey);
        queryClient.invalidateQueries(projectFoldersQueryKey);

        // Propagate invalidation to other screens/components
        invalidateSync(projectsQueryKey);
        invalidateSync(projectFoldersQueryKey);
      },
      onError: error => {
        console.error('Error deleting project', error);
      },
    }
  );
}
